import React, { useMemo, useState } from 'react';
import { CalendarDays, Download, FileSpreadsheet, Filter, TrendingUp, UploadCloud, X } from 'lucide-react';
import AISummary from '../components/analytics/AISummary';
import AnalyticsChart from '../components/analytics/AnalyticsChart';
import AnalyticsFilters from '../components/analytics/AnalyticsFilters';
import DataTable from '../components/analytics/DataTable';
import DataQualityPanel from '../components/analytics/DataQualityPanel';
import MetricCard from '../components/analytics/MetricCard';
import Button from '../components/ui/Button';
import {
  AnalyticsFilters as FilterState,
  createAnalyticsSnapshot,
  downloadAnalyticsSummary,
  downloadRowsAsCsv,
  formatValue,
  getStoredDataset,
} from '../services/analytics';

const AnalyticsPage: React.FC = () => {
  const dataset = getStoredDataset();
  const [filters, setFilters] = useState<FilterState>({});
  const snapshot = useMemo(() => (dataset ? createAnalyticsSnapshot(dataset, filters) : null), [dataset, filters]);
  const activeFilterLabel = getActiveChartFilterLabel(filters);

  if (!dataset || !snapshot) {
    return (
      <section className="page-stack" style={{ maxWidth: '900px' }}>
        <span className="icon-tile" style={{ width: '56px', height: '56px' }}>
          <TrendingUp size={28} />
        </span>
        <h1 style={{ fontSize: '2.35rem', marginBottom: '0.5rem' }}>No uploaded data yet</h1>
        <p>The Analytics page uses your real CSV or Excel data. Upload a file first so GabMic can detect columns and calculate results.</p>
        <a href="/upload"><Button variant="primary"><UploadCloud size={18} />Upload data</Button></a>
      </section>
    );
  }

  return (
    <div className="page-stack">
      <header className="page-header">
        <div>
          <h1 style={{ fontSize: '2.2rem', marginBottom: '0.55rem' }}>Explore your data</h1>
          <p className="page-kicker">Filter, compare, and understand the patterns GabMic detected.</p>
          <span className="file-chip" style={{ marginTop: '1rem' }}>
            <FileSpreadsheet size={17} />
            {dataset.fileName}
          </span>
        </div>
        {snapshot.comparison && (
          <span className="soft-chip">
            <CalendarDays size={16} />
            {snapshot.comparison.previousLabel} to {snapshot.comparison.currentLabel}
          </span>
        )}
      </header>

      <AnalyticsFilters dataset={dataset} filters={filters} onChange={setFilters} />

      <div className="premium-card" style={{ padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', minWidth: 0 }}>
          <span className="icon-tile" style={{ width: '34px', height: '34px' }}>
            <Filter size={16} />
          </span>
          <div>
            <strong style={{ display: 'block', fontSize: '0.95rem' }}>Interactive analysis</strong>
            <p style={{ margin: 0, fontSize: '0.86rem' }}>
              {activeFilterLabel ? `Filtered by ${activeFilterLabel}.` : 'Click a chart bar or point to filter the table.'}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap' }}>
          {activeFilterLabel && (
            <Button variant="ghost" size="sm" onClick={() => setFilters((current) => ({ ...current, categoryValue: undefined, startDate: undefined, endDate: undefined }))}>
              <X size={15} />
              Clear chart filter
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => downloadRowsAsCsv(dataset.fileName, dataset.columns, snapshot.filteredRows)}>
            <Download size={15} />
            Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadAnalyticsSummary(dataset.fileName, snapshot)}>
            <Download size={15} />
            Export summary
          </Button>
        </div>
      </div>

      <DataQualityPanel dataset={dataset} compact />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
        {snapshot.metrics.map((metric) => (
          <MetricCard key={metric.label} metric={metric} />
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
        <AnalyticsChart
          title="Date Trend"
          description="Grouped monthly totals when a date column is available."
          data={snapshot.timeSeries}
          metric={snapshot.metricColumn}
          currency={snapshot.currency}
          selectedLabel={filters.startDate && filters.endDate ? getMonthLabelFromRange(filters.startDate) : undefined}
          onPointSelect={(point) => {
            const range = getMonthRangeFromLabel(point.label);
            if (!range || !snapshot.dateColumn) return;
            setFilters((current) => ({
              ...current,
              dateColumn: snapshot.dateColumn?.name,
              startDate: range.startDate,
              endDate: range.endDate,
            }));
          }}
        />
        <AnalyticsChart
          title="Category Comparison"
          description="Top categories using the selected metric."
          data={snapshot.categorySeries}
          metric={snapshot.metricColumn}
          currency={snapshot.currency}
          selectedLabel={filters.categoryValue}
          onPointSelect={(point) => {
            if (!snapshot.categoryColumn) return;
            setFilters((current) => ({
              ...current,
              categoryColumn: snapshot.categoryColumn?.name,
              categoryValue: point.label,
            }));
          }}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
        <AISummary summary={snapshot.summary} />

        <div className="premium-card" style={{ padding: '1.5rem' }}>
          <span className="mono-label">Previous Period</span>
          {snapshot.comparison ? (
            <>
              <h3 style={{ fontSize: '2rem', marginBottom: '0.75rem', color: 'var(--gabmic-orange)' }}>{snapshot.comparison.formattedChange}</h3>
              <p style={{ margin: 0, fontSize: '0.95rem' }}>
                {snapshot.comparison.formattedCurrent} from {snapshot.comparison.currentLabel}, compared with {snapshot.comparison.formattedPrevious} from {snapshot.comparison.previousLabel}.
              </p>
            </>
          ) : (
            <p style={{ margin: 0, fontSize: '0.95rem' }}>Add a date column and metric column to compare this data with the previous period.</p>
          )}
        </div>
      </div>

      {snapshot.forecast && snapshot.metricColumn && (
        <div className="premium-card" style={{ padding: '1.5rem' }}>
          <span className="mono-label">Optional Forecast</span>
          <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>Simple next-period projection</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
            {snapshot.forecast.map((point) => (
              <div key={point.label} style={{ padding: '1rem', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'rgba(255,255,255,0.025)' }}>
                <span className="mono-label" style={{ marginBottom: '0.5rem' }}>{point.label}</span>
                <strong>{formatValue(point.value, snapshot.metricColumn, snapshot.currency)}</strong>
              </div>
            ))}
          </div>
        </div>
      )}

      <AnalyticsChart
        title="Numeric Columns"
        description="Totals across detected numeric columns."
        data={snapshot.numericBreakdown}
        metric={snapshot.metricColumn}
        currency={snapshot.currency}
        selectedLabel={filters.metricColumn}
        onPointSelect={(point) => {
          setFilters((current) => ({
            ...current,
            metricColumn: point.label,
          }));
        }}
      />

      <DataTable rows={snapshot.filteredRows} columns={dataset.columns} />
    </div>
  );
};

const getActiveChartFilterLabel = (filters: FilterState) => {
  if (filters.categoryValue) return filters.categoryValue;
  if (filters.startDate && filters.endDate) return `${filters.startDate} to ${filters.endDate}`;
  return '';
};

const getMonthRangeFromLabel = (label: string) => {
  const date = new Date(`1 ${label}`);
  if (Number.isNaN(date.getTime())) return null;

  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);

  return {
    startDate: toDateInputValue(start),
    endDate: toDateInputValue(end),
  };
};

const getMonthLabelFromRange = (startDate: string) => {
  const date = new Date(`${startDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return undefined;

  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const toDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default AnalyticsPage;
